import { Fragment, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Database,
  GitMerge,
  Plus,
} from "lucide-react";
import { AddEventDialog } from "../components/AddEventDialog";
import { InsertTouchpointMark } from "../components/InsertTouchpointMark";
import { MergeVisitorDialog } from "../components/MergeVisitorDialog";
import { capiLabel } from "../lib/capi-labels";
import {
  fetchCustomerBqPage,
  isMergeRedirect,
  useCustomer,
  useCustomerBqCount,
  type CustomerJourneyEvent,
} from "../lib/customers";
import { TableSkeleton } from "../components/states";

export function CustomerDetailPage() {
  const { projectId: projectIdStr, visitorId: visitorIdStr } = useParams();
  const projectId = projectIdStr ? parseInt(projectIdStr, 10) : null;
  const visitorId = visitorIdStr ? parseInt(visitorIdStr, 10) : null;
  const query = useCustomer(projectId, visitorId);
  const navigate = useNavigate();

  // If the backend says this visitor was merged elsewhere, hop to the
  // canonical id. Toast so the user understands the URL change.
  useEffect(() => {
    if (
      query.data &&
      projectId !== null &&
      isMergeRedirect(query.data) &&
      query.data.mergedInto !== visitorId
    ) {
      toast.info(
        `Visitor ${visitorId} wurde in ${query.data.mergedInto} gemergt — wechsle zur Canonical-Ansicht.`,
      );
      navigate(`/customers/${projectId}/${query.data.mergedInto}`, {
        replace: true,
      });
    }
  }, [query.data, projectId, visitorId, navigate]);

  return (
    <div className="px-6 py-8">
      <div className="mx-auto max-w-content space-y-6">
        <Link
          to={`/customers?project=${projectId}`}
          className="inline-flex items-center gap-1 text-xs text-text-tertiary hover:text-text-primary"
        >
          <ArrowLeft size={12} /> Customers
        </Link>

        {query.isLoading ? (
          <TableSkeleton rows={4} />
        ) : query.error ? (
          <div className="rounded-md border border-danger bg-danger-bg px-3 py-2 text-sm text-danger">
            {String((query.error as Error).message)}
          </div>
        ) : query.data &&
          !isMergeRedirect(query.data) &&
          projectId !== null &&
          visitorId !== null ? (
          <CustomerView
            data={query.data}
            projectId={projectId}
            visitorId={visitorId}
          />
        ) : null}
      </div>
    </div>
  );
}

function CustomerView({
  data,
  projectId,
  visitorId,
}: {
  data: import("../lib/customers").CustomerDetail;
  projectId: number;
  visitorId: number;
}) {
  // Selbe Display-Logik wie in der Liste: Email + Name kombiniert wenn
  // beide da, sonst was vorhanden ist. So bleibt die UI konsistent
  // zwischen Liste und Detail-Titel.
  const fullName = [data.leadDetails?.firstName, data.leadDetails?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const email = data.keys.find((k) => k.keyType === "email")?.value ?? null;
  const phone = data.keys.find((k) => k.keyType === "phone")?.value ?? null;
  let displayName: string;
  if (email && fullName) displayName = `${email} / ${fullName}`;
  else if (email) displayName = email;
  else if (fullName) displayName = fullName;
  else if (phone) displayName = phone;
  else displayName = "Anonymous";

  // BQ history loaded on demand. We track:
  //  - extraEvents: the rows we've already fetched from BQ
  //  - bqCursor: createdAt before which BQ should return — starts at the
  //    oldest PG event, advances as we paginate
  //  - bqHasMore: server tells us whether another page would yield rows
  const [addEventOpen, setAddEventOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  // TASK-75: insertion marks are opt-in — reading a journey is the normal
  // case, inserting a touchpoint the exception, so the marks start hidden
  // and only appear once this toggle is switched on.
  const [showInsertMarks, setShowInsertMarks] = useState(false);
  const [bqEvents, setBqEvents] = useState<CustomerJourneyEvent[]>([]);
  const [bqCursor, setBqCursor] = useState<string | null>(data.oldestPgEventAt);
  const [bqHasMore, setBqHasMore] = useState(true);
  const [bqLoading, setBqLoading] = useState(false);

  const bqCount = useCustomerBqCount(
    projectId,
    visitorId,
    data.oldestPgEventAt,
  );

  const loadMoreFromBq = async () => {
    setBqLoading(true);
    try {
      const page = await fetchCustomerBqPage(projectId, visitorId, bqCursor);
      setBqEvents((prev) => [...prev, ...page.items]);
      setBqCursor(page.nextCursor);
      setBqHasMore(page.hasMore);
      if (page.items.length === 0) {
        toast.info("Keine weiteren Events in BigQuery für diesen Visitor");
      }
    } catch (err) {
      toast.error(`BigQuery-Query fehlgeschlagen: ${(err as Error).message}`);
      setBqHasMore(false);
    } finally {
      setBqLoading(false);
    }
  };

  // Chronologische Reihenfolge: ältestes Event oben, neuestes unten.
  // Liest sich wie eine Timeline statt wie ein Feed. PG + BQ wieder mixen
  // und nach created_at aufsteigend sortieren — beide Quellen liefern
  // created_at als ISO-String, alphabetischer Sort entspricht zeitlichem.
  const combinedEvents: CustomerJourneyEvent[] = [
    ...data.journey,
    ...bqEvents,
  ].sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1));

  // Twin-Events eines gemergten CAPI-Sends (Browser-Regel + Webhook, gleiche
  // capiCanonicalId) zu EINER Zeile zusammenfassen — die Conversion ging auch
  // nur 1× an die Plattform. Das kanonische Event (dessen ID gesendet wurde)
  // führt die Zeile; die Twins bleiben im aufgeklappten Payload sichtbar.
  const groupedEvents: { event: CustomerJourneyEvent; twins: CustomerJourneyEvent[] }[] = [];
  const byCanonical = new Map<string, number>();
  for (const e of combinedEvents) {
    const cid = e.capiCanonicalId;
    const idx = cid ? byCanonical.get(cid) : undefined;
    if (!cid || idx === undefined) {
      if (cid) byCanonical.set(cid, groupedEvents.length);
      groupedEvents.push({ event: e, twins: [] });
      continue;
    }
    const g = groupedEvents[idx]!;
    if (e.eventId === cid) {
      g.twins.push(g.event);
      g.event = e;
    } else {
      g.twins.push(e);
    }
  }
  const olderInBq =
    bqCount.data && bqCount.data.bqMirrorEnabled
      ? Math.max(0, bqCount.data.count - bqEvents.length)
      : 0;

  return (
    <>
      <header className="flex items-start justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
            Customer
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-text-primary">
            {displayName}
          </h1>
          <p className="mt-0.5 font-mono text-xs text-text-tertiary">
            Visitor ID {data.visitorId} · {data.journey.length} event
            {data.journey.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setMergeOpen(true)}
            className="inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-secondary px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-bg-hover"
          >
            <GitMerge size={14} /> Merge with
          </button>
          <button
            type="button"
            onClick={() => setAddEventOpen(true)}
            className="inline-flex items-center gap-1 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-text-on-accent hover:bg-accent-hover"
          >
            <Plus size={14} /> Event hinzufügen
          </button>
        </div>
      </header>

      <AddEventDialog
        open={addEventOpen}
        onOpenChange={setAddEventOpen}
        projectId={projectId}
        fixedVisitor={{ visitorId: data.visitorId, label: displayName }}
      />
      <MergeVisitorDialog
        open={mergeOpen}
        onOpenChange={setMergeOpen}
        projectId={projectId}
        target={{ visitorId: data.visitorId, label: displayName }}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel title="Keys">
          {data.keys.length === 0 ? (
            <p className="text-xs text-text-tertiary">
              {data.testLead
                ? "Testeintrag („Test“ im Namen) — Keys werden hier bewusst nicht erfasst: der Kontakt bekommt ein frisches Profil und verbindet sich nie mit echten Personen. E-Mail und Telefon stehen weiterhin in der Event-Payload."
                : "Keine Keys erfasst."}
            </p>
          ) : (
            <ul className="space-y-1.5">
              {data.keys.map((k, i) => (
                <li
                  key={`${k.keyType}-${k.value}-${i}`}
                  className="flex gap-2 text-xs"
                >
                  <span className="w-20 flex-shrink-0 font-mono text-text-tertiary">
                    {k.keyType}
                  </span>
                  <span
                    className="flex-1 truncate font-mono text-text-primary"
                    title={k.originValue}
                  >
                    {k.value}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Attribution">
          {!data.attribution ? (
            <p className="text-xs text-text-tertiary">
              Keine UTM- oder Referrer-Signale beim Erst-Touch.
            </p>
          ) : (
            <ul className="space-y-1.5 text-xs">
              {data.attribution.referrerUrl && (
                <Row label="referrer_url" value={data.attribution.referrerUrl} />
              )}
              {data.attribution.utmSource && (
                <Row label="utm_source" value={data.attribution.utmSource} />
              )}
              {data.attribution.utmMedium && (
                <Row label="utm_medium" value={data.attribution.utmMedium} />
              )}
              {data.attribution.utmCampaign && (
                <Row label="utm_campaign" value={data.attribution.utmCampaign} />
              )}
              {data.attribution.utmContent && (
                <Row label="utm_content" value={data.attribution.utmContent} />
              )}
              {data.attribution.utmTerm && (
                <Row label="utm_term" value={data.attribution.utmTerm} />
              )}
              {data.attribution.landingPage && (
                <Row label="landing_page" value={data.attribution.landingPage} />
              )}
              <Row
                label="first_seen"
                value={new Date(data.attribution.firstSeenAt).toLocaleString(
                  "de-DE",
                )}
              />
            </ul>
          )}
        </Panel>

        {data.leadDetails && (
          <Panel title="Lead-Details">
            <ul className="space-y-1.5 text-xs">
              {data.leadDetails.firstName && (
                <Row label="firstName" value={data.leadDetails.firstName} />
              )}
              {data.leadDetails.lastName && (
                <Row label="lastName" value={data.leadDetails.lastName} />
              )}
              {data.leadDetails.company && (
                <Row label="company" value={data.leadDetails.company} />
              )}
              {data.leadDetails.country && (
                <Row label="country" value={data.leadDetails.country} />
              )}
              {data.leadDetails.industry && (
                <Row label="industry" value={data.leadDetails.industry} />
              )}
            </ul>
          </Panel>
        )}
      </div>

      <section className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h2 className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
            Customer Journey
          </h2>
          <button
            type="button"
            onClick={() => setShowInsertMarks((v) => !v)}
            aria-pressed={showInsertMarks}
            className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              showInsertMarks
                ? "bg-accent text-text-on-accent hover:bg-accent-hover"
                : "border border-border-primary bg-bg-secondary text-text-secondary hover:bg-bg-hover"
            }`}
          >
            <Plus size={14} /> Touchpoint einfügen
          </button>
        </div>
        <p className="text-xs text-text-tertiary">
          {data.journey.length} Events aus Postgres (35-Tage-Hot-Window)
          {bqEvents.length > 0 && (
            <>, {bqEvents.length} aus BigQuery nachgeladen</>
          )}
          {olderInBq > 0 && (
            <>
              {" "}
              · noch <strong>{olderInBq}</strong> ältere in BigQuery
              verfügbar
            </>
          )}
          . Chronologisch sortiert (alt → neu); klicken für die rohe
          Payload.
        </p>

        {/* Load-more sitzt OBEN, weil die Liste oldest-first ist und der
            Button noch ältere Events lädt — die landen oberhalb der bereits
            angezeigten. */}
        {data.bqMirrorEnabled && bqHasMore && (
          <div className="flex items-center justify-center">
            <button
              type="button"
              onClick={loadMoreFromBq}
              disabled={bqLoading || olderInBq === 0}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-primary bg-bg-secondary px-3 py-1.5 text-sm font-medium text-text-secondary transition-colors hover:bg-bg-hover disabled:opacity-50"
            >
              <Database size={12} />
              {bqLoading
                ? "Lade aus BigQuery…"
                : olderInBq === 0
                  ? "Keine weiteren Events"
                  : `↑ Ältere Events aus BigQuery laden (${olderInBq} verfügbar)`}
            </button>
          </div>
        )}
        {!data.bqMirrorEnabled && (
          <p className="rounded-md border border-border-secondary bg-bg-tertiary px-3 py-2 text-[11px] text-text-tertiary">
            BigQuery-Sync ist für dieses Projekt deaktiviert — ältere Events
            sind nicht abrufbar.
          </p>
        )}

        <ul className="space-y-1.5">
          {/* Insertion marks (TASK-59, A4): one before the first step, one
              after every step — so there's a mark between every pair plus
              the two ends, and a single one when the journey is empty.
              Only the frontmost mark gets olderCount/onLoadOlder (TASK-61,
              S6) — it's the only one that can ever hide unloaded history
              behind it. All marks stay hidden until the "Touchpoint
              einfügen" toggle above is switched on (TASK-75) — reading a
              journey is the normal case, inserting the exception. */}
          <InsertTouchpointMark
            projectId={projectId}
            visitorId={visitorId}
            prevAt={null}
            nextAt={groupedEvents[0]?.event.createdAt ?? null}
            visible={showInsertMarks}
            olderCount={olderInBq}
            onLoadOlder={loadMoreFromBq}
            loadingOlder={bqLoading}
          />
          {groupedEvents.map((g, i) => (
            <Fragment key={`${g.event.source}-${g.event.eventId}`}>
              <JourneyRow event={g.event} twins={g.twins} />
              <InsertTouchpointMark
                projectId={projectId}
                visitorId={visitorId}
                prevAt={g.event.createdAt}
                nextAt={groupedEvents[i + 1]?.event.createdAt ?? null}
                visible={showInsertMarks}
              />
            </Fragment>
          ))}
        </ul>
      </section>
    </>
  );
}

function Panel({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-md border border-border-primary bg-bg-secondary p-4">
      <h2 className="mb-3 font-mono text-xs uppercase tracking-wider text-text-tertiary">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <li className="flex gap-2">
      <span className="w-24 flex-shrink-0 font-mono text-text-tertiary">
        {label}
      </span>
      <span className="flex-1 break-all font-mono text-text-primary">{value}</span>
    </li>
  );
}

function JourneyRow({
  event,
  twins = [],
}: {
  event: import("../lib/customers").CustomerJourneyEvent;
  /** Twin events of the same merged CAPI send (browser + webhook channel),
   *  collapsed into this row. Their payloads show in the expanded view. */
  twins?: import("../lib/customers").CustomerJourneyEvent[];
}) {
  const [open, setOpen] = useState(false);
  return (
    <li className="rounded-md border border-border-primary bg-bg-secondary">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-bg-hover"
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <span
          className={`rounded px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase ${
            event.source === "bq"
              ? "bg-accent/15 text-accent"
              : "bg-bg-tertiary text-text-tertiary"
          }`}
          title={
            event.source === "bq"
              ? "Aus BigQuery nachgeladen (älter als das 35-Tage-Hot-Window)"
              : "Aus dem Postgres-Hot-Window"
          }
        >
          {event.source}
        </span>
        <span className="w-32 flex-shrink-0 text-sm font-medium text-text-primary">
          {event.eventType}
        </span>
        <span className="flex-1 truncate font-mono text-[11px] text-text-secondary">
          {event.eventSource ?? "—"}
        </span>
        {twins.length > 0 && (
          <span
            title={`${twins.length + 1} Kanäle (z. B. Browser-Regel + Webhook) intern zu einer Conversion zusammengeführt — nur 1× an die Plattform gesendet. Aufklappen zeigt beide Roh-Events.`}
            className="flex-shrink-0 rounded bg-violet-500/15 px-1.5 py-0.5 font-mono text-[9px] font-semibold text-violet-600 dark:text-violet-400"
          >
            dedupliziert ×{twins.length + 1}
          </span>
        )}
        <CapiLabels deliveries={event.capiDeliveries} />
        <span className="flex-shrink-0 font-mono text-[10px] text-text-tertiary">
          {new Date(event.createdAt).toLocaleString("de-DE")}
        </span>
      </button>
      {open && (
        <div className="max-h-[60vh] overflow-auto border-t border-border-primary bg-bg-tertiary">
          {[event, ...twins].map((e, i) => (
            <div key={e.eventId}>
              {twins.length > 0 && (
                <p className="px-3 pt-3 font-mono text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
                  {i === 0 ? "Kanonisches Event (an Plattform gesendet)" : `Zusammengeführter Twin ${i}`}
                </p>
              )}
              <pre className="p-3 font-mono text-[11px] leading-relaxed text-text-primary">
                {JSON.stringify(e.payload, null, 2)}
              </pre>
            </div>
          ))}
        </div>
      )}
    </li>
  );
}

/** Per-platform CAPI delivery chips next to a journey event: blue = Meta,
 *  green = Google, light-blue = LinkedIn. A failed send (ok=false) is shown
 *  struck-through in red so it stands out; a twin that was suppressed as a late
 *  duplicate is grey — the conversion IS at the platform, this row just wasn't
 *  re-sent (TASK-94). Renders nothing if the event was sent to no platform (or
 *  pre-dates the per-event delivery log), so a real outage still stands out. */
const PLATFORM_CLS: Record<string, string> = {
  meta: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  google: "bg-green-500/15 text-green-600 dark:text-green-400",
  linkedin: "bg-sky-400/15 text-sky-600 dark:text-sky-300",
};
function CapiLabels({
  deliveries,
}: {
  deliveries?: import("../lib/customers").CapiDelivery[];
}) {
  if (!deliveries || deliveries.length === 0) return null;
  return (
    <span className="flex flex-shrink-0 flex-wrap items-center gap-1">
      {deliveries.map((d) => {
        const { text, tone, platform } = capiLabel(d);
        const cls =
          tone === "failed"
            ? "bg-danger/15 text-danger line-through"
            : tone === "duplicate"
              ? "bg-bg-tertiary text-text-tertiary"
              : (PLATFORM_CLS[d.type] ?? "bg-bg-tertiary text-text-tertiary");
        return (
          <span
            key={d.type}
            title={
              tone === "failed"
                ? `An ${text} CAPI gesendet — abgelehnt`
                : tone === "duplicate"
                  ? `Dieselbe Conversion ging bereits über den anderen Kanal an ${platform} — diese Zeile wurde bewusst nicht erneut gesendet, damit die Plattform sie nur einmal zählt.`
                  : `An ${text} CAPI gesendet`
            }
            className={`rounded px-1.5 py-0.5 font-mono text-[9px] font-semibold ${cls}`}
          >
            {text}
          </span>
        );
      })}
    </span>
  );
}
